import { useEffect, useState } from "react";
import { adminUpdateSiteContent, fetchSiteContent } from "@/lib/api";
import { useSiteContentCtx } from "@/contexts/SiteContentContext";
import ImageUpload from "@/components/dashboard/ImageUpload";
import { toast } from "sonner";
import { FloppyDisk } from "@phosphor-icons/react";

export default function ContentEditor() {
  const ctx = useSiteContentCtx();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSiteContent().then(setData).catch(() => toast.error("Αποτυχία φόρτωσης"));
  }, []);

  if (!data) return <div className="text-white/50">Φόρτωση...</div>;

  const setSection = (section) => (next) => setData((d) => ({ ...d, [section]: typeof next === "function" ? next(d[section]) : next }));
  const updateField = (section, key) => (e) => setSection(section)((s) => ({ ...s, [key]: e.target ? e.target.value : e }));

  const save = async () => {
    setSaving(true);
    try {
      const updated = await adminUpdateSiteContent(data);
      ctx?.setContent(updated);
      toast.success("Αποθηκεύτηκε");
    } catch {
      toast.error("Αποτυχία αποθήκευσης");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="content-editor" className="space-y-8 max-w-4xl">
      <div className="flex items-end justify-between gap-4 sticky top-0 bg-[#0A0A0A] z-10 py-2">
        <div>
          <p className="text-overline mb-3">Περιεχόμενο</p>
          <h1 className="font-display font-black uppercase text-4xl tracking-tight">Επεξεργασία ιστοσελίδας</h1>
        </div>
        <button data-testid="content-save-btn" onClick={save} disabled={saving} className="btn-primary text-xs disabled:opacity-50">
          <FloppyDisk size={14} weight="bold" className="mr-2" />
          {saving ? "Αποθήκευση..." : "Αποθήκευση όλων"}
        </button>
      </div>

      {/* HERO */}
      <Card title="Hero (Αρχική)">
        <Row>
          <Field label="Established (μικρό)">
            <input className="dash-input" value={data.hero.established} onChange={updateField("hero", "established")} />
          </Field>
          <Field label="Τίτλος γραμμή 1">
            <input className="dash-input" value={data.hero.title_line1} onChange={updateField("hero", "title_line1")} />
          </Field>
          <Field label="Τίτλος (κόκκινο)">
            <input className="dash-input" value={data.hero.title_accent} onChange={updateField("hero", "title_accent")} />
          </Field>
        </Row>
        <Field label="Περιγραφή">
          <textarea rows={3} className="dash-input resize-none" value={data.hero.description} onChange={updateField("hero", "description")} />
        </Field>
        <Row>
          <Field label="Κουμπί 1"><input className="dash-input" value={data.hero.cta_primary} onChange={updateField("hero", "cta_primary")} /></Field>
          <Field label="Κουμπί 2"><input className="dash-input" value={data.hero.cta_secondary} onChange={updateField("hero", "cta_secondary")} /></Field>
        </Row>
        <ListEditor
          label="Stats κάτω (μικρές γραμμές)"
          items={data.hero.stats}
          onChange={(stats) => setSection("hero")((s) => ({ ...s, stats }))}
          empty={{ label: "" }}
          render={(item, set) => (
            <input className="dash-input" value={item.label} onChange={(e) => set({ label: e.target.value })} placeholder="π.χ. 60+ χρόνια" />
          )}
        />
        <ImageUpload testid="hero-video" label="Hero βίντεο/εικόνα (mp4 ή jpg)" value={data.hero.video_url} onChange={(url) => setSection("hero")((s) => ({ ...s, video_url: url }))} />
        <ImageUpload testid="hero-poster" label="Hero poster (πίσω από βίντεο)" value={data.hero.poster_url} onChange={(url) => setSection("hero")((s) => ({ ...s, poster_url: url }))} accept="image/*" />
      </Card>

      {/* SERVICES */}
      <Card title="Υπηρεσίες (3 πλακάκια)">
        <Row>
          <Field label="Overline"><input className="dash-input" value={data.services.overline} onChange={updateField("services", "overline")} /></Field>
          <Field label="Τίτλος γραμμή 1"><input className="dash-input" value={data.services.title_line1} onChange={updateField("services", "title_line1")} /></Field>
        </Row>
        <Row>
          <Field label="Λέξη"><input className="dash-input" value={data.services.title_line2} onChange={updateField("services", "title_line2")} /></Field>
          <Field label="Λέξη (κόκκινο)"><input className="dash-input" value={data.services.title_accent} onChange={updateField("services", "title_accent")} /></Field>
          <Field label="Τέλος τίτλου"><input className="dash-input" value={data.services.title_line3} onChange={updateField("services", "title_line3")} /></Field>
        </Row>
        <ListEditor
          label="Πλακάκια"
          items={data.services.items}
          onChange={(items) => setSection("services")((s) => ({ ...s, items }))}
          empty={{ title: "", desc: "" }}
          render={(item, set) => (
            <div className="space-y-2">
              <input className="dash-input" value={item.title} onChange={(e) => set({ ...item, title: e.target.value })} placeholder="Τίτλος" />
              <textarea rows={2} className="dash-input resize-none" value={item.desc} onChange={(e) => set({ ...item, desc: e.target.value })} placeholder="Περιγραφή" />
            </div>
          )}
        />
      </Card>

      {/* WORKSHOP */}
      <Card title="Εργαστήριο (αρχική)">
        <ImageUpload testid="workshop-image" label="Φωτογραφία εργαστηρίου" value={data.workshop.image_url} onChange={(url) => setSection("workshop")((s) => ({ ...s, image_url: url }))} accept="image/*" />
        <Row>
          <Field label="Overline"><input className="dash-input" value={data.workshop.overline} onChange={updateField("workshop", "overline")} /></Field>
          <Field label="Τίτλος γραμμή 1"><input className="dash-input" value={data.workshop.title_line1} onChange={updateField("workshop", "title_line1")} /></Field>
          <Field label="Λέξη (κόκκινο)"><input className="dash-input" value={data.workshop.title_accent} onChange={updateField("workshop", "title_accent")} /></Field>
        </Row>
        <Field label="Περιγραφή"><textarea rows={3} className="dash-input resize-none" value={data.workshop.description} onChange={updateField("workshop", "description")} /></Field>
        <Row>
          <Field label="Ταμπέλα χρόνου"><input className="dash-input" value={data.workshop.turnaround_label} onChange={updateField("workshop", "turnaround_label")} /></Field>
          <Field label="Τιμή χρόνου"><input className="dash-input" value={data.workshop.turnaround_value} onChange={updateField("workshop", "turnaround_value")} /></Field>
        </Row>
        <ListEditor
          label="3 ετικέτες (badges)"
          items={data.workshop.tags.map((t) => ({ label: t }))}
          onChange={(items) => setSection("workshop")((s) => ({ ...s, tags: items.map((i) => i.label) }))}
          empty={{ label: "" }}
          render={(item, set) => <input className="dash-input" value={item.label} onChange={(e) => set({ label: e.target.value })} />}
        />
      </Card>

      {/* CTA STRIP */}
      <Card title="CTA strip (κάτω από αρχική)">
        <Row>
          <Field label="Τίτλος γραμμή 1"><input className="dash-input" value={data.cta_strip.title_line1} onChange={updateField("cta_strip", "title_line1")} /></Field>
          <Field label="Τίτλος γραμμή 2"><input className="dash-input" value={data.cta_strip.title_line2} onChange={updateField("cta_strip", "title_line2")} /></Field>
        </Row>
        <Row>
          <Field label="Κουμπί 1"><input className="dash-input" value={data.cta_strip.primary} onChange={updateField("cta_strip", "primary")} /></Field>
          <Field label="Κουμπί 2"><input className="dash-input" value={data.cta_strip.secondary} onChange={updateField("cta_strip", "secondary")} /></Field>
        </Row>
      </Card>

      {/* ABOUT */}
      <Card title="Σχετικά (Γκαράζ)">
        <Row>
          <Field label="Overline"><input className="dash-input" value={data.about.overline} onChange={updateField("about", "overline")} /></Field>
          <Field label="Τίτλος γραμμή 1"><input className="dash-input" value={data.about.title_line1} onChange={updateField("about", "title_line1")} /></Field>
        </Row>
        <Row>
          <Field label="Τίτλος γραμμή 2"><input className="dash-input" value={data.about.title_line2} onChange={updateField("about", "title_line2")} /></Field>
          <Field label="Τίτλος (κόκκινο)"><input className="dash-input" value={data.about.title_accent} onChange={updateField("about", "title_accent")} /></Field>
        </Row>
        <Field label="Παράγραφος 1"><textarea rows={3} className="dash-input resize-none" value={data.about.story_p1} onChange={updateField("about", "story_p1")} /></Field>
        <Field label="Παράγραφος 2"><textarea rows={3} className="dash-input resize-none" value={data.about.story_p2} onChange={updateField("about", "story_p2")} /></Field>
        <ListEditor
          label="4 stats (νούμερα)"
          items={data.about.stats}
          onChange={(stats) => setSection("about")((s) => ({ ...s, stats }))}
          empty={{ value: "", label: "" }}
          render={(item, set) => (
            <Row>
              <input className="dash-input" value={item.value} onChange={(e) => set({ ...item, value: e.target.value })} placeholder="60+" />
              <input className="dash-input" value={item.label} onChange={(e) => set({ ...item, label: e.target.value })} placeholder="Χρόνια στις πίστες" />
            </Row>
          )}
        />
        <Row>
          <Field label="Story Overline"><input className="dash-input" value={data.about.story_overline} onChange={updateField("about", "story_overline")} /></Field>
          <Field label="Story τίτλος"><input className="dash-input" value={data.about.story_title} onChange={updateField("about", "story_title")} /></Field>
        </Row>
        <Field label="Story κείμενο"><textarea rows={3} className="dash-input resize-none" value={data.about.story_full} onChange={updateField("about", "story_full")} /></Field>
        <ImageUpload testid="story-image" label="Story εικόνα" value={data.about.story_image_url} onChange={(url) => setSection("about")((s) => ({ ...s, story_image_url: url }))} accept="image/*" />
        <ListEditor
          label="3 pills"
          items={data.about.story_pills.map((t) => ({ label: t }))}
          onChange={(items) => setSection("about")((s) => ({ ...s, story_pills: items.map((i) => i.label) }))}
          empty={{ label: "" }}
          render={(item, set) => <input className="dash-input" value={item.label} onChange={(e) => set({ label: e.target.value })} />}
        />
        <Row>
          <Field label="Team overline"><input className="dash-input" value={data.about.team_overline} onChange={updateField("about", "team_overline")} /></Field>
          <Field label="Team τίτλος"><input className="dash-input" value={data.about.team_title} onChange={updateField("about", "team_title")} /></Field>
        </Row>
        <ListEditor
          label="Ομάδα"
          items={data.about.team}
          onChange={(team) => setSection("about")((s) => ({ ...s, team }))}
          empty={{ name: "", role: "", img: "" }}
          render={(item, set) => (
            <div className="space-y-2">
              <Row>
                <input className="dash-input" value={item.name} onChange={(e) => set({ ...item, name: e.target.value })} placeholder="Όνομα" />
                <input className="dash-input" value={item.role} onChange={(e) => set({ ...item, role: e.target.value })} placeholder="Ρόλος" />
              </Row>
              <ImageUpload testid={`team-img`} label="Φωτογραφία" value={item.img} onChange={(url) => set({ ...item, img: url })} accept="image/*" />
            </div>
          )}
        />
      </Card>

      {/* CONTACT */}
      <Card title="Επικοινωνία">
        <Row>
          <Field label="Overline"><input className="dash-input" value={data.contact.overline} onChange={updateField("contact", "overline")} /></Field>
          <Field label="Τίτλος"><input className="dash-input" value={data.contact.title} onChange={updateField("contact", "title")} /></Field>
        </Row>
        <Row>
          <Field label="Διεύθυνση"><input className="dash-input" value={data.contact.address} onChange={updateField("contact", "address")} /></Field>
          <Field label="Τηλέφωνο"><input className="dash-input" value={data.contact.phone} onChange={updateField("contact", "phone")} /></Field>
        </Row>
        <Row>
          <Field label="Email"><input className="dash-input" value={data.contact.email} onChange={updateField("contact", "email")} /></Field>
          <Field label="Ωράριο"><input className="dash-input" value={data.contact.hours} onChange={updateField("contact", "hours")} /></Field>
        </Row>
      </Card>

      {/* TOP STRIP */}
      <Card title="Top Strip (πάνω πάνω: 2 βίντεο/φωτο + λογότυπο στη μέση)">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={!!data.top_strip?.enabled}
            onChange={(e) => setSection("top_strip")((s) => ({ ...s, enabled: e.target.checked }))}
            className="w-5 h-5 accent-[#E50000]"
          />
          <span className="text-sm text-white/70">Εμφάνιση Top Strip</span>
        </label>
        <ImageUpload
          testid="top-strip-logo"
          label="Λογότυπο (στη μέση του strip)"
          value={data.top_strip?.logo_url || ""}
          onChange={(url) => setSection("top_strip")((s) => ({ ...s, logo_url: url || "/logo.png" }))}
          accept="image/*"
        />
        {(data.top_strip?.panels || []).map((p, i) => (
          <div key={i} className="border border-white/10 p-4 bg-[#0E0E0E] space-y-3">
            <p className="text-overline">Slot {i + 1}</p>
            <ImageUpload
              testid={`top-strip-media-${i}`}
              label="Background: φωτογραφία ή βίντεο (mp4/jpg/png)"
              value={p.media_url}
              onChange={(url) => {
                const panels = [...(data.top_strip?.panels || [])];
                panels[i] = { ...panels[i], media_url: url };
                setSection("top_strip")((s) => ({ ...s, panels }));
              }}
            />
            <ImageUpload
              testid={`top-strip-caption-image-${i}`}
              label="Λογότυπο / Εικόνα caption (προαιρετικό — αντικαθιστά το κείμενο)"
              value={p.caption_image_url || ""}
              onChange={(url) => {
                const panels = [...(data.top_strip?.panels || [])];
                panels[i] = { ...panels[i], caption_image_url: url };
                setSection("top_strip")((s) => ({ ...s, panels }));
              }}
              accept="image/*"
            />
            <Row>
              <Field label="Caption κείμενο (αν δεν υπάρχει εικόνα)">
                <input className="dash-input" value={p.caption || ""} onChange={(e) => {
                  const panels = [...(data.top_strip?.panels || [])];
                  panels[i] = { ...panels[i], caption: e.target.value };
                  setSection("top_strip")((s) => ({ ...s, panels }));
                }} />
              </Field>
              <Field label="Link URL (πού πάει κλικ)">
                <input className="dash-input" value={p.link_url || ""} onChange={(e) => {
                  const panels = [...(data.top_strip?.panels || [])];
                  panels[i] = { ...panels[i], link_url: e.target.value };
                  setSection("top_strip")((s) => ({ ...s, panels }));
                }} placeholder="/thrust ή https://..." />
              </Field>
            </Row>
          </div>
        ))}
      </Card>

      {/* SOCIAL BAR */}
      <Card title="Social Media Bar (μεγάλα εικονίδια πάνω από footer)">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={!!data.social_bar?.enabled}
            onChange={(e) => setSection("social_bar")((s) => ({ ...s, enabled: e.target.checked }))}
            className="w-5 h-5 accent-[#E50000]"
          />
          <span className="text-sm text-white/70">Εμφάνιση Social Bar</span>
        </label>
        <Row>
          <Field label="Overline">
            <input className="dash-input" value={data.social_bar?.heading || ""} onChange={updateField("social_bar", "heading")} />
          </Field>
          <Field label="Τίτλος">
            <input className="dash-input" value={data.social_bar?.subtitle || ""} onChange={updateField("social_bar", "subtitle")} />
          </Field>
        </Row>
        <ListEditor
          label="Social platforms (άφησε URL κενό για να μη φαίνεται)"
          items={data.social_bar?.items || []}
          onChange={(items) => setSection("social_bar")((s) => ({ ...s, items }))}
          empty={{ platform: "instagram", url: "" }}
          render={(item, set) => (
            <Row>
              <select className="dash-input" value={item.platform || "instagram"} onChange={(e) => set({ ...item, platform: e.target.value })}>
                <option value="instagram">Instagram</option>
                <option value="facebook">Facebook</option>
                <option value="youtube">YouTube</option>
                <option value="tiktok">TikTok</option>
                <option value="twitter">X / Twitter</option>
                <option value="linkedin">LinkedIn</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="telegram">Telegram</option>
                <option value="pinterest">Pinterest</option>
                <option value="email">Email</option>
                <option value="web">Website</option>
              </select>
              <input className="dash-input" value={item.url || ""} onChange={(e) => set({ ...item, url: e.target.value })} placeholder="https://... ή email@..." />
            </Row>
          )}
        />
      </Card>

      {/* POPUP */}
      <Card title="Popup (εμφανίζεται όταν μπαίνει κάποιος στο site)">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={!!data.popup?.enabled}
            onChange={(e) => setSection("popup")((s) => ({ ...(s || {}), enabled: e.target.checked }))}
            className="w-5 h-5 accent-[#E50000]"
          />
          <span className="text-sm text-white/70">Εμφάνιση Popup</span>
        </label>
        <Row>
          <Field label="Τίτλος">
            <input className="dash-input" value={data.popup?.title || ""} onChange={updateField("popup", "title")} />
          </Field>
          <Field label="Link κουμπιού">
            <input className="dash-input" value={data.popup?.button_link || ""} onChange={updateField("popup", "button_link")} placeholder="/thrust ή https://..." />
          </Field>
        </Row>
        <Field label="Μήνυμα">
          <textarea rows={3} className="dash-input resize-none" value={data.popup?.message || ""} onChange={updateField("popup", "message")} />
        </Field>
        <Row>
          <Field label="Κείμενο κουμπιού">
            <input className="dash-input" value={data.popup?.button_text || ""} onChange={updateField("popup", "button_text")} />
          </Field>
        </Row>
        <ImageUpload
          testid="popup-image"
          label="Εικόνα popup (προαιρετικό)"
          value={data.popup?.image_url || ""}
          onChange={(url) => setSection("popup")((s) => ({ ...(s || {}), image_url: url }))}
          accept="image/*"
        />
        <p className="text-xs text-white/40">
          Το popup εμφανίζεται μία φορά ανά session. Όταν αλλάξεις τίτλο/μήνυμα/εικόνα, εμφανίζεται ξανά στους επισκέπτες.
        </p>
      </Card>

      {/* FOOTER */}
      <Card title="Footer">
        <Field label="Tagline (πάνω)"><textarea rows={2} className="dash-input resize-none" value={data.footer.tagline} onChange={updateField("footer", "tagline")} /></Field>
        <Field label="Tagline (κάτω)"><input className="dash-input" value={data.footer.tagline_bottom} onChange={updateField("footer", "tagline_bottom")} /></Field>
        <Row>
          <Field label="Instagram URL"><input className="dash-input" value={data.footer.social.instagram} onChange={(e) => setSection("footer")((s) => ({ ...s, social: { ...s.social, instagram: e.target.value } }))} /></Field>
          <Field label="Facebook URL"><input className="dash-input" value={data.footer.social.facebook} onChange={(e) => setSection("footer")((s) => ({ ...s, social: { ...s.social, facebook: e.target.value } }))} /></Field>
          <Field label="Youtube URL"><input className="dash-input" value={data.footer.social.youtube} onChange={(e) => setSection("footer")((s) => ({ ...s, social: { ...s.social, youtube: e.target.value } }))} /></Field>
        </Row>
      </Card>

      <div className="sticky bottom-4 flex justify-end">
        <button data-testid="content-save-btn-bottom" onClick={save} disabled={saving} className="btn-primary text-xs disabled:opacity-50 shadow-lg">
          <FloppyDisk size={14} weight="bold" className="mr-2" />
          {saving ? "Αποθήκευση..." : "Αποθήκευση όλων"}
        </button>
      </div>

      <style>{`
        .dash-input {
          width: 100%;
          background: #1A1A1A;
          border: 1px solid rgba(255,255,255,0.15);
          color: white;
          padding: 0.7rem 0.9rem;
          font-family: 'Manrope', sans-serif;
          font-size: 0.9rem;
          outline: none;
          border-radius: 2px;
        }
        .dash-input:focus { border-color: #E50000; }
      `}</style>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <section className="border border-white/10 bg-[#141414] p-6 md:p-8 space-y-5">
      <h2 className="font-display font-black uppercase text-2xl tracking-tight border-b border-white/10 pb-3">{title}</h2>
      {children}
    </section>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-white/60 mb-2">{label}</span>
      {children}
    </label>
  );
}

function ListEditor({ label, items, onChange, render, empty }) {
  const update = (i, next) => {
    const copy = [...items];
    copy[i] = next;
    onChange(copy);
  };
  const add = () => onChange([...items, empty]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">{label}</span>
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={i} className="border border-white/10 p-3 bg-[#0E0E0E] flex gap-3 items-start">
            <div className="flex-1">{render(item, (next) => update(i, next))}</div>
            <button type="button" onClick={() => remove(i)} className="text-xs text-white/50 hover:text-[#E50000] uppercase tracking-wider">×</button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="text-xs uppercase tracking-wider text-white/60 hover:text-[#E50000] border border-dashed border-white/15 px-3 py-2">
        + Προσθήκη
      </button>
    </div>
  );
}
